import { CheckIcon, CloseIcon } from "@xola/icons";
import { Logo } from "@xola/ui-kit";
import clsx from "clsx";
import { now } from "common/src/helpers/dateHelper";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import React from "react";
import { useMutation } from "react-query";
import {
    AnnouncementFeedback,
    AnnouncementType,
    FeatureRole,
    Announcement as IAnnouncement,
    Installation,
} from "types";
import { api } from "../../api/api";
import { useSeller } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { useInstallations } from "../../hooks/usePlugins";
import { hasFeature } from "../../models/Seller";
import { SecondaryButton } from "../Common/SecondaryButton";
import { Loader } from "../Loader";
import { AnnouncementActions } from "./AnnouncementActions";
import { getDefaultPhotoUrl, getPhotoUrl } from "./AnnouncementsDrawer";

export const AnnouncementBanner = () => {
    const { data: announcements, isLoading } = useCollection<IAnnouncement>("announcements", { status: "active" });

    const { data: installations, isLoading: isLoadingInstallations } = useInstallations();

    if (isLoading || isLoadingInstallations) {
        return <Loader />;
    }

    const validBanners = getValidBanners(announcements, installations);

    return validBanners.length > 0 ? <Announcement announcement={validBanners[0]} /> : null;
};

const getValidBanners = (announcements: IAnnouncement[], installations: Installation[]) => {
    return announcements.filter(
        (announcement) =>
            isValidForUser(announcement, installations) &&
            announcement.types.includes(AnnouncementType.BANNER) &&
            dayjs(announcement.end).isSameOrAfter(now()),
    );
};

enum FeedbackType {
    POSITIVE = "positive",
    NEGATIVE = "negative",
}

interface CreateFeedbackPayload {
    seller: { id: ID };
    text: FeedbackType;
    type: string;
}

const Announcement = ({ announcement }: { announcement: IAnnouncement }) => {
    const seller = useSeller();

    const isLiveChatAvailable = hasFeature(seller, FeatureRole.XCHAT);

    const invalidate = useInvalidate();

    const { data: announcementFeedbacks, isLoading } = useCollection<AnnouncementFeedback>("announcementFeedbacks", {
        announcement: announcement.id,
    });

    const { mutateAsync: createFeedback } = useMutation(async (payload: CreateFeedbackPayload) => {
        await api.post(`/announcements/${announcement.id}/feedback?seller=${seller.id}`, payload);
    });

    if (isLoading) {
        return <Loader />;
    }

    const handleFeedback = async (feedback: FeedbackType) => {
        const payload = {
            seller: { id: seller.id },
            text: feedback,
            type: "announcement",
        };
        await createFeedback(payload);
        invalidate("announcementFeedbacks");
    };

    if (announcementFeedbacks.length > 0) {
        return null;
    }

    return (
        <div
            className={clsx(
                "max-w-11/12 fixed right-0 bottom-0 z-50 m-4 flex flex-col rounded-lg bg-blue-lighter p-4 md:left-24 lg:w-10/12 xl:left-50",
                { "w-2/3": isLiveChatAvailable },
            )}
        >
            <div className="flex items-center justify-between">
                <div className="flex space-x-2">
                    <Logo size="medium" src={announcement.photo ? getPhotoUrl(announcement) : getDefaultPhotoUrl()} />
                    <div className="flex-col space-y-1">
                        <h4 className="font-bold">{announcement.title}</h4>
                        <div>{announcement.description}</div>
                        <div className="flex-reverse flex">
                            <AnnouncementActions
                                announcement={announcement}
                                className="flex-row-reverse items-center"
                            />
                        </div>
                    </div>
                </div>
                <div className="flex space-x-2">
                    <SecondaryButton
                        size="small"
                        color="secondary"
                        onClick={async () => handleFeedback(FeedbackType.POSITIVE)}
                    >
                        <CheckIcon />
                    </SecondaryButton>
                    <SecondaryButton
                        size="small"
                        color="secondary"
                        onClick={async () => handleFeedback(FeedbackType.NEGATIVE)}
                    >
                        <CloseIcon />
                    </SecondaryButton>
                </div>
            </div>
        </div>
    );
};

const isValidForUser = (announcement: IAnnouncement, installations: Installation[]) => {
    // If no plugin filters is specified, then display the announcement to the user.
    if (isEmpty(announcement.pluginIds)) {
        return true;
    }

    for (const installation of installations) {
        if (announcement.pluginIds.includes(installation.plugin)) {
            return true;
        }
    }

    return false;
};
